<script context="module" lang="ts">
    type BaseItemType<T> = {
        label: string;
        value: T;
        icon?: string;
    };

    type ParentItemType<T> = {
        children?: ItemType<T>[];
    };

    export type ItemType<T> = BaseItemType<T> & ParentItemType<T>;
</script>

<script lang="ts" generics="TValues, TValue">
    import type { Field, Form } from '@jmvdevelop/svelte-form';

    import { createDropdownMenu } from '@melt-ui/svelte';
    import { fly } from 'svelte/transition';
    import { defaultButtonClass, defaultIconClass } from './Button.svelte';
    import * as t from '$trans';
    import { cx } from '$lib/utils/cx';
    import WithTooltip from '$components/Tooltip/WithTooltip.svelte';
    import { melt } from '@melt-ui/svelte';
    import { getContainer } from '$components/Context/ContainerContext';

    export let placeholder: string;
    export let items: ItemType<TValue>[];
    export let form: Form<TValues, string>;
    export let field: Field<TValues, TValue | null>;
    export let portal: HTMLElement | null = null;
    //export let toText: (value: TValue) => string | null | undefined;
    //export let fromText: (string: string) => TValue;

    let value = form.stores().fieldValue(field);

    let selection: ItemType<TValue> | null = valueToItem($value);
    $: selection = valueToItem($value);

    $: form.dispatch(
        form
            .actions()
            .changeValue(
                selection == null ? null : isLeaf(selection) ? selection.value : null,
                field,
            ),
    );

    const { lang } = getContainer();

    function clear() {
        selection = null;
    }

    function isLeaf(item: ItemType<TValue>) {
        return item.children == null || item.children.length == 0;
    }

    function isParent(item: ItemType<TValue>) {
        return item.children != null && item.children.length > 0;
    }

    function findParent(value: TValue): ItemType<TValue> | null {
        const flattenItems = (
            list: ItemType<TValue>[],
            parent: ItemType<TValue> | null,
        ): { parent: ItemType<TValue> | null; item: ItemType<TValue> }[] => {
            return list.flatMap((it) => {
                if (it.children)
                    return [{ item: it, parent }, ...flattenItems(it.children, it)];
                return { item: it, parent };
            });
        };

        return (
            flattenItems(items, null).find((it) => it.item.value === value)?.parent ??
            null
        );
    }

    function flattenItems(list: ItemType<TValue>[]): ItemType<TValue>[] {
        return list.flatMap((it) => {
            if (it.children) return [it, ...flattenItems(it.children)];
            return it;
        });
    }

    function valueToItem(value: TValue | null) {
        return flattenItems(items).find((it) => it.value === value) ?? null;
    }

    $: parent =
        selection == null
            ? null
            : isLeaf(selection)
              ? findParent(selection.value)
              : selection;
    $: list =
        selection == null
            ? items
            : isLeaf(selection)
              ? (findParent(selection.value)?.children ?? items)
              : (selection.children ?? items);
    $: selectionLabel = selection != null && isLeaf(selection) ? selection.label : null;
    $: selectionValue = selection != null && isLeaf(selection) ? selection.value : null;

    const {
        elements: { trigger, menu, item },
        states: { open },
    } = createDropdownMenu({
        positioning: {
            placement: 'bottom-start',
            sameWidth: false,
            gutter: 4,
        },
        forceVisible: true,
        loop: true,
        onOpenChange: ({ next }) => {
            if (next === false) {
                if (selection != null && isLeaf(selection)) selection = { ...selection };
                else selection = null;
            }
            return next;
        },
        portal,
    });
</script>

<div class="flex gap-[4px] w-[145px]">
    <WithTooltip
        openDelay={0}
        closeDelay={0}
        zIndex="inc"
        tooltipClass="max-w-[95vw] t:max-w-[500px]"
    >
        <div
            slot="default"
            let:trigger={tooltipTrigger}
            use:melt={tooltipTrigger}
            class="flex min-w-0"
        >
            <button
                type="button"
                class={cx(defaultButtonClass, 'truncate min-w-0')}
                {...$trigger}
                use:trigger
            >
                {#if selectionLabel == null}
                    <i
                        class="{defaultIconClass} tnp tnp-list-bullet"
                        aria-hidden={true}
                    />
                {/if}
                <span class="truncate flex-1 text-left"
                    >{selectionLabel ?? placeholder}</span
                >
                <i class="{defaultIconClass} tnp-chevron-down" aria-hidden={true} />
            </button>
        </div>

        <svelte:fragment slot="tooltip">
            <div class="text-left line-clamp-5">
                {selectionLabel ?? placeholder}
            </div>
        </svelte:fragment>
    </WithTooltip>

    {#if selection != null}
        <button
            class={cx(defaultButtonClass, 'size-[30px]')}
            on:click|preventDefault={() => clear()}
        >
            <i class="tnp tnp-cross" aria-hidden={true}></i>
            <span class="sr-only">{t.ui.clear_input({}, $lang)}</span>
        </button>
    {/if}
</div>

{#if $open}
    <div
        class="dropdown-menu-leo"
        {...$menu}
        use:menu
        transition:fly={{ duration: 150, y: -10 }}
    >
        {#if parent != null}
            <div class="dropdown-menu-leo__header">
                <button
                    class="dropdown-menu-leo__back-button"
                    type="button"
                    aria-label={t.ui.back_to_return({}, $lang)}
                    on:click|preventDefault={() => {
                        if (parent != null) {
                            parent = findParent(parent.value);
                            list = parent?.children ?? items;
                        }
                    }}
                    ><i
                        class="{defaultIconClass} tnp tnp-chevron-left"
                        aria-hidden={true}
                    />
                </button>
                <span class="grow">{parent.label}</span>
            </div>
        {/if}

        {#each list as it}
            <div
                class="dropdown-menu-leo__item"
                on:m-click={(e) => {
                    if (isParent(it)) e.preventDefault();
                    selection = { ...it };
                }}
                {...$item}
                use:item
            >
                {#if it.icon != null}
                    <i class="{defaultIconClass} {it.icon}" aria-hidden={true} />
                {:else if isLeaf(it)}
                    <input
                        type="radio"
                        class="m-0"
                        checked={selectionValue == it.value}
                    />
                {/if}
                <span class="grow">{it.label}</span>
                {#if isParent(it)}
                    <i
                        class="{defaultIconClass} tnp tnp-chevron-right"
                        aria-hidden={true}
                    />
                {/if}
            </div>
        {/each}
    </div>
{/if}
